import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import type { TimerHandle, WebSocketClient } from './client';
import { getWebSocketClient } from './index';

export interface WebSocketLifecycleDeps {
  client?: Pick<WebSocketClient, 'connect' | 'disconnect'>;
  eventTarget?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  isOnline?: () => boolean;
  setTimeoutImpl?: (handler: () => void, timeoutMs: number) => TimerHandle;
  clearTimeoutImpl?: (handle: TimerHandle) => void;
}

let disposeLifecycle: (() => void) | null = null;

export const disposeWebSocketLifecycle = (): void => {
  disposeLifecycle?.();
  disposeLifecycle = null;
};

export const initWebSocketLifecycle = (
  deps: WebSocketLifecycleDeps = {},
): (() => void) => {
  if (disposeLifecycle) return disposeLifecycle;

  const client = deps.client ?? getWebSocketClient();
  const eventTarget = deps.eventTarget ?? window;
  const isOnline = deps.isOnline ?? (() => navigator.onLine);
  const setTimeoutImpl = deps.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = deps.clearTimeoutImpl ?? clearTimeout;
  let foreground = true;
  let disposed = false;
  let connectTimer: TimerHandle | null = null;

  const cancelConnect = (): void => {
    if (connectTimer !== null) {
      clearTimeoutImpl(connectTimer);
      connectTimer = null;
    }
  };

  // Debounced so a burst of lifecycle events - network recovery arriving
  // alongside foregrounding - produces one attempt rather than one each.
  const scheduleConnect = (): void => {
    if (disposed || !foreground || !isOnline() || connectTimer !== null) return;

    connectTimer = setTimeoutImpl(() => {
      connectTimer = null;
      if (!disposed && foreground && isOnline()) client.connect();
    }, 100);
  };

  const onOnline = (): void => scheduleConnect();
  const onResume = (): void => {
    foreground = true;
    scheduleConnect();
  };
  const onPause = (): void => {
    foreground = false;
    cancelConnect();
    client.disconnect();
  };
  const onOffline = (): void => {
    cancelConnect();
    client.disconnect();
  };

  eventTarget.addEventListener('online', onOnline);
  eventTarget.addEventListener('offline', onOffline);
  // Foreground/background comes from the App plugin rather than the event
  // target: the web implementation maps it onto document visibility, so it
  // reports the same states in the browser as on a device.
  const appStateListeners: Promise<PluginListenerHandle>[] = [
    CapacitorApp.addListener('resume', onResume),
    CapacitorApp.addListener('pause', onPause),
  ];
  scheduleConnect();

  disposeLifecycle = () => {
    if (disposed) return;
    disposed = true;
    cancelConnect();
    eventTarget.removeEventListener('online', onOnline);
    eventTarget.removeEventListener('offline', onOffline);
    for (const listener of appStateListeners) {
      void listener.then((handle) => handle.remove());
    }
    client.disconnect();
  };

  return disposeLifecycle;
};
